import { Injectable, Injector, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { interval, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { SharedService } from './shared.service';

/**
 * Detects when the running SPA is out of date relative to the deployed build
 * and forces stale clients to refresh.
 *
 * How it works:
 *  - `environment.version` is baked into the bundle at build time.
 *  - `/version.json` is served fresh (no-cache) and always reflects the
 *    currently-deployed build (kept in sync by scripts/generate-version.js).
 *  - On load — and periodically thereafter — we fetch /version.json and
 *    compare it to the baked-in version. A mismatch means a newer build has
 *    been deployed while this tab was open (or the browser served a cached
 *    shell), so we clear caches, log the user out and hard-reload to pull the
 *    new bundle.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /** How often to re-check while a tab stays open (production only). */
  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
  private static readonly VERSION_URL = 'version.json';
  /** Records which server version we last reloaded for (survives the reload). */
  private static readonly RELOAD_GUARD_KEY = 'versionCheck:reloadedFor';

  private handlingMismatch = false;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    private injector: Injector
  ) {}

  /** Call once from AppComponent. */
  /** Call once from AppComponent. */
  public init(): void {
    // Run the startup check in every environment. The one-shot reload guard
    // (see handleVersionMismatch) makes this loop-safe even on the dev server,
    // where the live-rebuilt bundle and the once-generated version.json drift
    // apart — it will log out / reload at most once per server version.
    this.check();

    // Background polling and focus re-checks only matter for a real deployment
    // (in dev, ng serve's live-reload already re-runs the startup check).
    if (!environment.production) {
      return;
    }

    // Prevent duplicate interval/listener registration if init() is called again.
    const initKey = 'versionCheck:initialized';
    try {
      if (sessionStorage.getItem(initKey) === '1') {
        return;
      }
      sessionStorage.setItem(initKey, '1');
    } catch {
      /* ignore — best effort */
    }

    this.zone.runOutsideAngular(() => {
      interval(VersionCheckService.POLL_INTERVAL_MS).subscribe(() =>
        this.zone.run(() => this.check())
      );
    });

    window.addEventListener('focus', () => this.check());
  }

  private check(): void {
    if (this.handlingMismatch || !environment.version) {
      return;
    }

    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    });

    // Cache-busting query param defends against any intermediary cache.
    this.http
      .get<{ version: string }>(
        `${VersionCheckService.VERSION_URL}?t=${Date.now()}`,
        { headers }
      )
      .pipe(
        map(res => res && res.version),
        catchError(() => of(null))
      )
      .subscribe(serverVersion => {
        if (!serverVersion) {
          return;
        }
        if (serverVersion === environment.version) {
          // Up to date — clear any stale loop-guard from a previous update.
          this.setReloadGuard(null);
          return;
        }
        this.handleVersionMismatch(serverVersion);
      });
  }

  private handleVersionMismatch(serverVersion: string): void {
    if (this.handlingMismatch) {
      return;
    }

    // Loop guard: if we already reloaded for this exact server version and the
    // running bundle still hasn't caught up (stale CDN/cache, mid-deploy, or a
    // version/version.json mismatch), stop instead of reloading forever.
    if (this.getReloadGuard() === serverVersion) {
      // eslint-disable-next-line no-console
      console.warn(
        `[VersionCheck] Already reloaded for "${serverVersion}" but bundle ` +
          `is still "${environment.version}". Skipping further reloads.`
      );
      return;
    }

    this.handlingMismatch = true;

    // eslint-disable-next-line no-console
    console.warn(
      `[VersionCheck] Running version "${environment.version}" is stale; ` +
        `deployed version is "${serverVersion}". Clearing caches and logging out.`
    );

    this.clearBrowserCaches()
      .then(() => this.logOut())
      .finally(() => {
        // Set the guard AFTER clearing caches (clear wipes sessionStorage),
        // so it survives the hard reload that follows.
        this.setReloadGuard(serverVersion);
        this.hardReload();
      });
  }

  private getReloadGuard(): string | null {
    try {
      return sessionStorage.getItem(VersionCheckService.RELOAD_GUARD_KEY);
    } catch {
      return null;
    }
  }

  private setReloadGuard(value: string | null): void {
    try {
      if (value === null) {
        sessionStorage.removeItem(VersionCheckService.RELOAD_GUARD_KEY);
      } else {
        sessionStorage.setItem(VersionCheckService.RELOAD_GUARD_KEY, value);
      }
    } catch {
      /* ignore — best effort */
    }
  }

  private async clearBrowserCaches(): Promise<void> {
    try {
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map(key => caches.delete(key)));
      }
    } catch {
      /* ignore — best effort */
    }
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* ignore — best effort */
    }
  }

  private logOut(): void {
    try {
      // Use the existing logout flow so cookies/session state are cleared.
      this.injector.get(SharedService).logOut();
    } catch {
      /* ignore — the hard reload below is the real safeguard */
    }
  }

  private hardReload(): void {
    // Full document navigation forces the browser to refetch index.html
    // (served no-cache) and the new fingerprinted bundles.
    window.location.href = `${window.location.origin}/?v=${Date.now()}`;
  }
}
